import React from "react";
import { styled } from "@mui/system";
import FiatWithdrawalIcon from "src/icons/FiatWithdrawalIcon";
import TableAuthorisedIcon from "src/icons/TableAuthorisedIcon";
import TableCompletedIcon from "src/icons/TableCompletedIcon";
import TablePendingIcon from "src/icons/TablePendingIcon";
import TableTargetOrderOpen from "src/icons/TableTargetOrderOpenIcon";
import TradeIcon from "src/icons/TradeIcon";
import AirDropIcon from "src/icons/transactions/AirDropIcon";
import BuyIcon from "src/icons/transactions/BuyIcon";
import CancelledIcon from "src/icons/transactions/CancelledIcon";
import CryptoDepositIcon from "src/icons/transactions/CryptoDepositIcon";
import CryptoWithdrawalIcon from "src/icons/transactions/CryptoWithdrawalIcon";
import FiatDepositIcon from "src/icons/transactions/FiatDepositIcon";
import TargetIcon from "src/icons/transactions/TargetIcon";
import { CurrencyTransactionRes, PaginatedCTsRes } from "src/store/features/api/generated";
import { Currency } from "./types";
import SignUpBonusIcon from "src/icons/SignUpBonusIcon";
import PromotionIcon from "src/icons/PromotionIcon";
import StopLimitIcon from "src/icons/StopLimitIcon";
import { endOfMonth, format, isWithinInterval, startOfMonth } from "date-fns";
import PriceAlertIcon from "src/icons/transactions/PriceAlertIcon";
import ReferAFriendIcon from "src/icons/transactions/ReferAFriendIcon";
import { ContactChangePhoneIcon } from "src/icons/ContactChangePhoneIcon";
import { ContactChangeEmailIcon } from "src/icons/ContactChangeEmailIcon";
import LockIcon from "src/icons/LockIcon";
import UnlockIcon from "src/icons/UnlockIcon";

export const ContactChangeIconWrapper = styled("div")<{ background?: string }>(
  ({ background }) => ({
    display: "flex",
    padding: "8px",
    justifyContent: "center",
    alignItems: "center",
    gap: "4px",
    borderRadius: "20px",
    background: background || "#F0FEF9",
  }),
);

export const AccountLockIconWrapper = styled("div")(() => ({
  display: "flex",
  padding: "8px",
  justifyContent: "center",
  alignItems: "center",
  gap: "4px",
  borderRadius: "20px",
  background: "#F5F3EF",
}));

/**
 * Returns the number of currency transactions in the given array that were created in the current
 * month.
 *
 * @param data An array of currency transaction objects
 * @returns The number of currency transactions that were created in the current month
 */
export const getNumOfTransactionsThisMonth = (data?: CurrencyTransactionRes[]): number => {
  if (!data) return 0;

  // Get the start and end of the current month
  const currentDate = new Date();
  const startOfMonthDate = startOfMonth(currentDate);
  const endOfMonthDate = endOfMonth(currentDate);

  // Filter out the objects that were not created this month
  const newObjects = data.filter((obj) => {
    const createdDate = new Date(obj.created);

    return isWithinInterval(createdDate, { start: startOfMonthDate, end: endOfMonthDate });
  });

  // Get the number of new objects
  const numNewObjects = newObjects.length;
  return numNewObjects;
};

/**
 *
 * @param date a date string
 * @returns a date string formatted as day-month-year
 */
export const dateStringConversion = (date: string): string => {
  return date.replace(/\//g, "-");
};

/**
 * Returns the appropriate icon element for the given notification type.
 *
 * @param subType The notification subtype string
 * @param type The notification type string
 * @param message The notification message string
 * @returns The appropriate icon element for the given notification type
 */
export const iconSelector = (subType: string, type?: string, message?: string): JSX.Element => {
  if (type === "CONTACT_CHANGE") {
    let background = "#F0FEF9";
    if (message?.includes("submitted!")) {
      background = "#FEFCF8";
    } else if (message?.includes("incomplete")) {
      background = "#FFF6F6";
    } else {
      background = "#F0FEF9";
    }

    if (subType === "PHONE") {
      return (
        <ContactChangeIconWrapper background={background}>
          <ContactChangePhoneIcon />
        </ContactChangeIconWrapper>
      );
    } else {
      return (
        <ContactChangeIconWrapper background={background}>
          <ContactChangeEmailIcon />
        </ContactChangeIconWrapper>
      );
    }
  }

  if (type === "ACCOUNT_LOCK") {
    if (subType === "LOCKED") {
      return (
        <AccountLockIconWrapper>
          <LockIcon />
        </AccountLockIconWrapper>
      );
    } else {
      return (
        <AccountLockIconWrapper>
          <UnlockIcon />
        </AccountLockIconWrapper>
      );
    }
  }

  switch (subType) {
    case "Target Order":
    case "TARGET_ORDER":
    case "Target":
      return <TargetIcon />;

    case "Trade Order":
    case "TRADE_ORDER":
    case "Trade":
    case "DCA":
      return <TradeIcon height="32" width="32" />;

    case "Buy Order":
    case "BUY_ORDER":
    case "Sell Order":
    case "SELL_ORDER":
      return <BuyIcon />;

    case "Fiat Deposit":
    case "FIAT_DEPOSIT":
    case "Deposit":
      return <FiatDepositIcon />;

    case "Fiat Withdrawal":
    case "FIAT_WITHDRAWAL":
    case "Withdrawal":
      return <FiatWithdrawalIcon height="32px" />;

    case "Crypto Deposit":
    case "CRYPTO_DEPOSIT":
      return <CryptoDepositIcon />;

    case "Crypto Withdrawal":
    case "CRYPTO_WITHDRAWAL":
      return <CryptoWithdrawalIcon />;

    case "Sign Up Bonus":
      return <SignUpBonusIcon />;

    case "Referral Reward":
      return <PromotionIcon />;

    case "REFERRAL_REWARD":
      return <ReferAFriendIcon />;

    case "Stop Limit Order":
      return <StopLimitIcon />;

    case "Airdrop":
    case "AIRDROP":
      return <AirDropIcon />;

    case "Price Alert":
    case "ALERT":
      return <PriceAlertIcon />;

    case "Referral":
      return <ReferAFriendIcon />;

    default:
      return <TargetIcon />;
  }
};

/**
 * Returns an array containing a JSX element and a string representing the status
 * of an item based on the specified type string.
 *
 * @param type - The type string to use in determining the item status.
 * @returns An array containing a JSX element representing the item status and a string
 * representing the item status text.
 */
export const statusSelector = (type: string): [JSX.Element, string] => {
  switch (true) {
    case type.includes("Testing Pending"):
      return [<TablePendingIcon />, "Testing Pending"];
    case type.includes("Pending"):
      return [<TablePendingIcon />, "Pending"];
    case type.includes("Email 2FA required"):
      return [<TablePendingIcon />, "Email 2FA required"];
    case type.includes("Requested"):
      return [<TablePendingIcon />, "Requested"];
    case type.includes("Completed"):
      return [<TableCompletedIcon />, "Completed"];
    case type.includes("Cancelled"):
      return [<CancelledIcon />, "Cancelled"];
    case type.includes("Open"):
      return [<TableTargetOrderOpen />, "Open"];
    case type.includes("Authorised"):
      return [<TableAuthorisedIcon />, "Authorised"];
    case type.includes("Processing"):
      return [<TableAuthorisedIcon />, "Processing"];
    case type.includes("Verified"):
      return [<TableCompletedIcon />, "Verified"];
    default:
      return [<></>, ""];
  }
};

/**
 * Formats a timestamp as a date string in the format 2 Mar 2023.
 *
 * @param timestamp - The timestamp to format as a date string.
 * @returns The formatted date string.
 */
export const getDate = (timestamp: number): string => {
  const date = new Date(timestamp);

  // Use 'format' to format the date as a string in the desired format
  const formattedDate = format(date, "d MMM yyyy");
  return formattedDate;
};

/**
 * Formats a timestamp as a time string in the format "hh:mm AM/PM".
 *
 * @param timestamp - The timestamp to format as a time string.
 * @returns The formatted time string.
 */
export const getTime = (timestamp: number): string => {
  const date = new Date(timestamp);

  // Use 'format' to format the time as a string in the desired format
  const formattedTime = format(date, "hh:mm a");
  return formattedTime;
};

/**
 * Creates an object representing the line items for a transaction.
 *
 * @param side - The side of the transaction.
 * @param quote - The quote currency.
 * @param base - The base currency or currencies.
 * @param units - The number of units exchanged.
 * @param price - The price per unit.
 * @param value - The total value of the transaction.
 * @param fee - The transaction fee.
 * @returns An object representing the line items for a transaction.
 */
export function createLinesDesktop(
  side: string,
  quote: string,
  base: string[],
  units: string | 0,
  price: string,
  value: string,
  fee: string,
) {
  return {
    side,
    quote,
    base,
    units,
    price,
    value,
    fee,
  };
}
/**
 * Creates a new data object with the given properties.
 *
 * @param id - The ID of the data object.
 * @param icon - The icon JSX element of the data object.
 * @param type - The type of the data object.
 * @param date - The date of the data object.
 * @param time - The time of the data object.
 * @param currency - The currency of the data object.
 * @param amount - The amount of the data object.
 * @param status - The status of the data object represented as a tuple with a JSX element and a string.
 * @param lines - An array of line items for the data object.
 * @param note - An optional note for the data object.
 * @returns A new data object with the given properties.
 */
export function createDataDesktop(
  id: string,
  icon: JSX.Element,
  type: string,
  date: string,
  time: string,
  currency: { code: string; img: string }[],
  amount: string,
  status: [JSX.Element, string],
  lines: ReturnType<typeof createLinesDesktop>[],
  note?: string,
) {
  return {
    id,
    icon,
    type,
    date,
    time,
    currency,
    amount,
    status,
    note,
    lines: lines,
  };
}

export function createLinesMobile(
  side: string,
  type: string,
  quote: Currency,
  base: Currency,
  units: string | 0,
  price: string,
  value: string,
  fee: string,
) {
  return {
    side,
    type,
    quote,
    base,
    units,
    price,
    value,
    fee,
  };
}

export function createDataMobile(
  id: string,
  icon: JSX.Element,
  type: string,
  date: string,
  time: string,
  currency: string,
  amount: string,
  status: [JSX.Element, string],
  lines: ReturnType<typeof createLinesMobile>[],
  note?: string,
  /**
   * Travel Rule orchestrator status (D6). Optional — present only on
   * transactions whose backing `notabeneTransfers` row is in a TR-relevant
   * state. Consumed by `TransactionItem` to render a `TransferStatusPill`.
   */
  trStatus?: string,
) {
  return {
    id,
    icon,
    type,
    date,
    time,
    currency,
    amount,
    status,
    lines: lines,
    note,
    trStatus,
  };
}

/**
 * Returns an array containing the lowest 'start' key value and the highest 'end' key value
 * from a transactions object's 'bins' array.
 * @param transactions An object containing a 'bins' property which is an array of objects with
 *   'start', 'end', and 'freq' properties.
 * @returns An array containing the lowest 'start' key value and the highest 'end' key value.
 *   If the 'transactions' parameter is null or undefined, an empty array will be returned.
 */
export const useTransactionLowHigh = (transactions: PaginatedCTsRes | undefined): string[] => {
  const lowHigh = React.useMemo(() => {
    if (!transactions) return [];

    const bins = [...transactions.bins]; // bypass read-only error when trying to sort data
    const sortedData = bins.sort((a, b) => a.start - b.start);

    const lowestStart = sortedData[0].start;
    const highestEnd = sortedData[sortedData.length - 1].end;

    return [lowestStart?.toString() ?? "Min", highestEnd?.toString() ?? "Max"];
  }, [transactions]);

  return lowHigh;
};
